/**
 * The shape every endpoint answers in, mirrored from the backend.
 *
 * The server wraps both outcomes in one envelope - see `src/common/utils.ts`
 * over in the backend - so a client can branch on `success` alone and never
 * has to guess where the payload lives.
 *
 * These types are hand-mirrored rather than generated. If that drifts, the
 * place it shows up is `client.ts`, which is the only file that ever sees an
 * envelope: every service function above it receives `data` already unwrapped.
 */

export type ApiErrorDetail = {
  field?: string;
  message: string;
  code?: string;
};

export type ApiResponse<T> = {
  success: boolean;
  statusCode: number;
  message: string;
  data: T | null;
  errors?: ApiErrorDetail[] | null;
  timestamp: string;
  path?: string;
};

/**
 * A failed request, in one type.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY FAILURE BECOMES ONE OF THESE
 * ---------------------------------------------------------------------------
 * Four completely different things can go wrong, and a screen has to handle
 * all of them the same way or it handles none of them:
 *
 *   the phone has no signal          fetch rejects
 *   the server is not running        fetch rejects, differently
 *   the request hung                 nothing settles at all
 *   the server said no               a perfectly good 401 with a message
 *
 * Left alone, the first three arrive as opaque `TypeError`s whose messages are
 * useless to a customer ("Network request failed"), and the fourth arrives as
 * a successful response nobody thought to check. Normalising them here is what
 * lets a screen write `catch (e) { setError(e) }` and have that be correct.
 *
 * `message` is always safe to show. The server writes its own messages for a
 * customer to read; the client-side ones below are written the same way.
 */
export class ApiError extends Error {
  /** HTTP status, or 0 when the request never reached a server. */
  readonly status: number;
  /** Field-level failures from the validation pipe, when there are any. */
  readonly errors: ApiErrorDetail[];
  /** The failure never got a reply - no signal, wrong address, timed out. */
  readonly isNetwork: boolean;

  constructor(
    message: string,
    status = 0,
    errors: ApiErrorDetail[] = [],
    isNetwork = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errors = errors;
    this.isNetwork = isNetwork;
  }

  /**
   * The token is missing, expired or rejected.
   *
   * The one status the app must never merely display: it means sign the user
   * out and ask again, wherever it happens.
   */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Asked for a code too soon, or guessed wrong too many times. */
  get isThrottled(): boolean {
    return this.status === 429;
  }
}
